import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../../layoutcontainers/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import MaterialTypography from "../../../../components/MaterialTypography";
import MaterialBox from "../../../../components/MaterialBox";
import MaterialInput from "../../../../components/MaterialInput";
import MaterialButton from "../../../../components/MaterialButton";
import MaterialSelect from "../../../../components/MasterSelect";
import MaterialDataGrid from "../../../../components/MaterialDataGrid";
import ConfirmationDialog from "../../../../components/Dialog/ConfirmationDialog";
import axios from "axios";
import BASEURL from "../../../Utils/Baseurl";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLoading } from "../../../../context/loading";
import useBranchAccess from "../../../../hooks/useBranchAccess";

const formatTime12hr = (timeStr) => {
  if (!timeStr) return "-";
  const parts = timeStr.split(":");
  const hour = parseInt(parts[0], 10);
  const min = parts[1];
  const ampm = hour >= 12 ? "PM" : "AM";
  const hr12 = hour % 12 || 12;
  return `${hr12}:${min} ${ampm}`;
};

const ShiftRoster = () => {
  const navigate = useNavigate();
  const { setCirleLoading } = useLoading();
  const { isBranchAdmin, userBranch } = useBranchAccess();

  // Form state for creating roster
  const [formData, setFormData] = useState({
    selectedEmployees: [],
    selectedShift: null,
    startDate: "",
    endDate: "",
    rosterType: { value: "scheduled", label: "Scheduled" },
    remarks: "",
  });

  // Options
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);

  // Roster list/table
  const [rosterList, setRosterList] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterEmployee, setFilterEmployee] = useState(null);
  const [filterShift, setFilterShift] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    showCancel: true,
    showConfirm: true,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const rosterTypeOptions = [
    { value: "scheduled", label: "Scheduled" },
    { value: "swapped", label: "Swapped" },
    { value: "override", label: "Override" },
  ];

  useEffect(() => {
    fetchEmployees();
    fetchShifts();
  }, []);

  const fetchEmployees = async () => {
    try {
      const branchesVar = isBranchAdmin && userBranch ? [userBranch] : [];
      const response = await axios.post(
        BASEURL,
        {
          query: `query($branches: [String]) { allUsersDetail(branches: $branches) { id firstName lastName empid category } }`,
          variables: { branches: branchesVar },
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const users = response.data?.data?.allUsersDetail || [];
      const options = users.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName} (${u.empid})`,
        empid: u.empid,
      }));
      setEmployeeOptions(options);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await axios.post(
        BASEURL,
        {
          query: `query { allShifts { id name startTime endTime shiftType } }`,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const shifts = response.data?.data?.allShifts || [];
      const options = shifts.map((s) => ({
        value: s.id,
        label: `${s.name} ${
          s.startTime && s.endTime
            ? `(${formatTime12hr(s.startTime)} - ${formatTime12hr(s.endTime)})`
            : ""
        }`,
        name: s.name,
      }));
      setShiftOptions(options);
    } catch (err) {
      console.error("Error fetching shifts:", err);
    }
  };

  const fetchRosterData = async () => {
    if (!filterStartDate || !filterEndDate) {
      Swal.fire({
        icon: "warning",
        title: "Please select start and end dates to view roster.",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }
    setTableLoading(true);
    try {
      let variables = {
        startDate: filterStartDate,
        endDate: filterEndDate,
      };
      if (filterEmployee) variables.employeeId = filterEmployee.value;
      if (filterShift) variables.shiftId = filterShift.value;

      const response = await axios.post(
        BASEURL,
        {
          query: `query ShiftRosterByDateRange(
            $startDate: Date!,
            $endDate: Date!,
            $employeeId: ID,
            $shiftId: ID
          ) {
            shiftRosterByDateRange(
              startDate: $startDate,
              endDate: $endDate,
              employeeId: $employeeId,
              shiftId: $shiftId
            ) {
              id
              date
              rosterType
              remarks
              employeeName
              employeeEmpid
              shiftName
              shiftStartTime
              shiftEndTime
            }
          }`,
          variables,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data?.data?.shiftRosterByDateRange || [];
      setRosterList(data);
    } catch (err) {
      console.error("Error fetching roster:", err);
    } finally {
      setTableLoading(false);
    }
  };

  const handleCreateRoster = async () => {
    setCirleLoading(true);
    try {
      const employeeIds = formData.selectedEmployees.map((e) => e.value);
      const response = await axios.post(
        BASEURL,
        {
          query: `mutation CreateShiftRoster(
            $employeeIds: [ID]!,
            $shiftId: ID!,
            $startDate: Date!,
            $endDate: Date!,
            $rosterType: String,
            $remarks: String
          ) {
            createShiftRoster(
              employeeIds: $employeeIds,
              shiftId: $shiftId,
              startDate: $startDate,
              endDate: $endDate,
              rosterType: $rosterType,
              remarks: $remarks
            ) {
              success
              message
              createdCount
              skippedCount
            }
          }`,
          variables: {
            employeeIds,
            shiftId: formData.selectedShift.value,
            startDate: formData.startDate,
            endDate: formData.endDate,
            rosterType: formData.rosterType?.value || "scheduled",
            remarks: formData.remarks || "",
          },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data?.data?.createShiftRoster;
      if (result?.success) {
        Swal.fire({
          icon: "success",
          title: "Roster Created!",
          text: result.message,
          showConfirmButton: false,
          timer: 3000,
        });
        // Reset form
        setFormData({
          selectedEmployees: [],
          selectedShift: null,
          startDate: "",
          endDate: "",
          rosterType: { value: "scheduled", label: "Scheduled" },
          remarks: "",
        });
        // Refresh table if filters are set
        if (filterStartDate && filterEndDate) {
          fetchRosterData();
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result?.message || "Failed to create roster.",
          showConfirmButton: false,
          timer: 2500,
        });
      }
      setConfirmationDialog({ ...confirmationDialog, open: false });
    } catch (err) {
      console.error("Error creating roster:", err);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Something went wrong. Please try again.",
        showConfirmButton: false,
        timer: 2500,
      });
    } finally {
      setCirleLoading(false);
    }
  };

  const handleDeleteRoster = async (rosterId) => {
    setConfirmationDialog({
      open: true,
      title: "Delete Roster Entry",
      message: "Are you sure you want to delete this roster entry?",
      showCancel: true,
      showConfirm: true,
      onConfirm: async () => {
        setConfirmationDialog({ ...confirmationDialog, open: false });
        setCirleLoading(true);
        try {
          const response = await axios.post(
            BASEURL,
            {
              query: `mutation DeleteShiftRoster($ids: [ID]) {
                deleteShiftRoster(ids: $ids) {
                  success
                  message
                  deletedCount
                }
              }`,
              variables: { ids: [rosterId] },
            },
            { headers: { "Content-Type": "application/json" } }
          );
          const result = response.data?.data?.deleteShiftRoster;
          if (result?.success) {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: result.message,
              showConfirmButton: false,
              timer: 2000,
            });
            fetchRosterData();
          }
        } catch (err) {
          console.error("Error deleting roster:", err);
        } finally {
          setCirleLoading(false);
        }
      },
      onCancel: () =>
        setConfirmationDialog({ ...confirmationDialog, open: false }),
    });
  };

  const onConfirmCreate = () => {
    const empCount = formData.selectedEmployees.length;
    setConfirmationDialog({
      open: true,
      title: "Confirm Roster Creation",
      message: `Create roster for ${empCount} employee(s) from ${formData.startDate} to ${formData.endDate}?`,
      showCancel: true,
      showConfirm: true,
      onConfirm: () => handleCreateRoster(),
      onCancel: () =>
        setConfirmationDialog({ ...confirmationDialog, open: false }),
    });
  };

  const isFormValid = () => {
    return (
      formData.selectedEmployees.length > 0 &&
      formData.selectedShift &&
      formData.startDate &&
      formData.endDate &&
      formData.startDate <= formData.endDate
    );
  };

  const rosterColumns = [
    {
      field: "date",
      headerName: "Date",
      flex: 0.8,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) => {
        if (!params.value) return "-";
        const d = new Date(params.value);
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      field: "employeeName",
      headerName: "Employee",
      flex: 1.2,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "employeeEmpid",
      headerName: "Emp ID",
      flex: 0.7,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "shiftName",
      headerName: "Shift",
      flex: 1,
      disableColumnMenu: true,
      sortable: true,
    },
    {
      field: "timing",
      headerName: "Timing",
      flex: 1.2,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const { shiftStartTime, shiftEndTime } = params.row;
        if (shiftStartTime && shiftEndTime) {
          return `${formatTime12hr(shiftStartTime)} - ${formatTime12hr(
            shiftEndTime
          )}`;
        }
        return "Duration Only";
      },
    },
    {
      field: "rosterType",
      headerName: "Type",
      flex: 0.7,
      disableColumnMenu: true,
      sortable: true,
      renderCell: (params) => {
        const colorMap = {
          scheduled: "primary",
          swapped: "warning",
          override: "error",
        };
        return (
          <Chip
            label={params.value || "scheduled"}
            color={colorMap[params.value] || "default"}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.4,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRoster(params.row.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rosterRows = rosterList.map((r) => ({
    id: r.id,
    date: r.date,
    employeeName: r.employeeName,
    employeeEmpid: r.employeeEmpid,
    shiftName: r.shiftName,
    shiftStartTime: r.shiftStartTime,
    shiftEndTime: r.shiftEndTime,
    rosterType: r.rosterType,
    remarks: r.remarks,
  }));

  return (
    <DashboardLayout>
      {confirmationDialog.open && (
        <ConfirmationDialog
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          showCancel={confirmationDialog.showCancel}
          showConfirm={confirmationDialog.showConfirm}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
        />
      )}

      {/* Create Roster Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <MaterialTypography variant="h5" fontWeight="medium">
                Create Shift Roster
              </MaterialTypography>
              <MaterialButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => navigate("/shift")}
              >
                Back to Shifts
              </MaterialButton>
            </Stack>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            {/* Employees Multi-Select */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Select Employees <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="employees"
                  isMulti={true}
                  value={formData.selectedEmployees}
                  options={employeeOptions}
                  onChange={(selected) =>
                    setFormData({
                      ...formData,
                      selectedEmployees: selected || [],
                    })
                  }
                  isClearable
                  isSearchable
                  customStyles={{ optionPadding: 20 }}
                />
              </MaterialBox>
            </Grid>

            {/* Shift Select */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Select Shift <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="shift"
                  value={formData.selectedShift}
                  options={shiftOptions}
                  onChange={(selected) =>
                    setFormData({ ...formData, selectedShift: selected })
                  }
                  isClearable
                  isSearchable
                  customStyles={{ optionPadding: 20 }}
                />
              </MaterialBox>
            </Grid>

            {/* Start Date */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Start Date <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                />
              </MaterialBox>
            </Grid>

            {/* End Date */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    End Date <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                />
              </MaterialBox>
            </Grid>

            {/* Roster Type */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Roster Type
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="rosterType"
                  value={formData.rosterType}
                  options={rosterTypeOptions}
                  onChange={(selected) =>
                    setFormData({ ...formData, rosterType: selected })
                  }
                  customStyles={{ optionPadding: 20 }}
                />
              </MaterialBox>
            </Grid>

            {/* Remarks */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Remarks
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="remarks"
                  placeholder="Optional remarks"
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData({ ...formData, remarks: e.target.value })
                  }
                />
              </MaterialBox>
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <MaterialBox display="flex" justifyContent="flex-end" mt={1}>
                <MaterialButton
                  variant="gradient"
                  color="dark"
                  disabled={!isFormValid()}
                  onClick={onConfirmCreate}
                >
                  Create Roster
                </MaterialButton>
              </MaterialBox>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Roster Table Card */}
      <Card>
        <CardHeader
          title={
            <MaterialTypography variant="h5" fontWeight="medium">
              Shift Roster List
            </MaterialTypography>
          }
        />
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    From Date <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="filterStartDate"
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </MaterialBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    To Date <span style={{ color: "red" }}>*</span>
                  </MaterialTypography>
                </MaterialBox>
                <MaterialInput
                  name="filterEndDate"
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </MaterialBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 2.5 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Employee
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="filterEmployee"
                  value={filterEmployee}
                  options={employeeOptions}
                  onChange={(selected) => setFilterEmployee(selected)}
                  isClearable
                  isSearchable
                  customStyles={{ optionPadding: 20 }}
                />
              </MaterialBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 2.5 }}>
              <MaterialBox>
                <MaterialBox lineHeight={0} display="inline-block">
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Shift
                  </MaterialTypography>
                </MaterialBox>
                <MaterialSelect
                  name="filterShift"
                  value={filterShift}
                  options={shiftOptions}
                  onChange={(selected) => setFilterShift(selected)}
                  isClearable
                  isSearchable
                  customStyles={{ optionPadding: 20 }}
                />
              </MaterialBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 1 }}>
              <MaterialBox mt={3}>
                <MaterialButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  onClick={fetchRosterData}
                  disabled={!filterStartDate || !filterEndDate}
                >
                  Search
                </MaterialButton>
              </MaterialBox>
            </Grid>
          </Grid>

          {/* Data Grid */}
          <MaterialDataGrid
            rows={rosterRows}
            columns={rosterColumns}
            checkboxSelection={false}
            isSelectable={false}
            loading={tableLoading}
            localeText={{
              noRowsLabel: (
                <Stack
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <MaterialTypography
                    variant="body2"
                    fontWeight="medium"
                    color="text"
                  >
                    No roster entries found
                  </MaterialTypography>
                  <MaterialTypography variant="caption" color="text">
                    Select a date range and click "Search" to view roster data.
                    Use the form above to create new roster entries.
                  </MaterialTypography>
                </Stack>
              ),
            }}
          />
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default ShiftRoster;
